import { useCallback, useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import useUserUpdate from '../api/useUserUpdate';
import { AppContext } from '../../contexts';

const FILTER_QUERY_KEYS = ['sort', 'ages', 'chars', 'minPrice', 'maxPrice', 'wMin', 'wMax'];

const CATEGORY_PATHNAMES = ['/category/[link]', '/igrashki'];

const stripFilterQuery = (query: Record<string, any>) => {
  const cleaned = { ...query };
  FILTER_QUERY_KEYS.forEach((k) => delete cleaned[k]);
  return cleaned;
};

const stripFilterAsPath = (asPath: string) => {
  const [path, qs] = asPath.split('?');
  if (!qs) return path;
  const params = new URLSearchParams(qs);
  FILTER_QUERY_KEYS.forEach((k) => params.delete(k));
  const cleanQs = params.toString();
  return cleanQs ? `${path}?${cleanQs}` : path;
};

const useLanguageCheck = () => {
  const { locale, push, pathname, query, asPath } = useRouter();
  const { updateLanguage } = useUserUpdate();
  const { user, upsertUser } = useContext(AppContext);
  const { i18n } = useTranslation();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.lang && locale !== user?.lang) {
      i18n.changeLanguage(user?.lang);
      push({ pathname, query: stripFilterQuery(query) }, stripFilterAsPath(asPath), { locale: user?.lang });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, user?.lang]);

  const changeLang = useCallback((newLocale: string) => {
    setLoading(true);

    // On category/igrashki pages strip all filter queries to avoid router.replace conflicts
    const isCategoryPage = CATEGORY_PATHNAMES.includes(pathname);
    const cleanQuery = isCategoryPage
      ? Object.fromEntries(Object.entries(query).filter(([k]) => !FILTER_QUERY_KEYS.includes(k) && k !== 'currency'))
      : stripFilterQuery(query);
    const cleanAsPath = isCategoryPage ? asPath.split('?')[0] : stripFilterAsPath(asPath);

    if (user) {
      return updateLanguage(newLocale).then(() => {
        user.lang = newLocale;
        upsertUser(user);
        i18n.changeLanguage(newLocale);
        push({ pathname, query: cleanQuery }, cleanAsPath, { locale: newLocale });
        setLoading(false);
      }).catch(() => setLoading(false));
    }
    i18n.changeLanguage(newLocale);
    push({ pathname, query: cleanQuery }, cleanAsPath, { locale: newLocale });
    setLoading(false);
  }, [asPath, i18n, pathname, push, query, updateLanguage, upsertUser, user]);

  return {
    changeLang,
    changeLangLoading: loading,
  };
};

export default useLanguageCheck;
